/**
 * NOS-134 M14: Public registration / waitlist form for events with registration_enabled.
 */

import { useCallback, useEffect, useMemo, useState } from "react"
import { supabase } from "@/lib/supabase"
import { FunctionsHttpError } from "@supabase/supabase-js"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Card, CardContent } from "@/components/ui/card"
import { Loader2 } from "lucide-react"

export interface EventRegistrationTicketType {
  id: string
  name: string
  seats_per_ticket: number
  /** NOS-242: cents — feeds the Event JSON-LD offers; optional for callers
   * that don't select it. */
  price_cents?: number | null
}

export interface EventRegistrationFormEvent {
  id: string
  title: string
  start_date: string
  registration_enabled: boolean
  waitlist_enabled: boolean
  registration_deadline: string | null
  capacity: number | null
}

export interface EventRegistrationFormProps {
  orgId: string
  event: EventRegistrationFormEvent
  ticketTypes: EventRegistrationTicketType[]
  primaryColor: string
}

type Outcome = "confirmed" | "waitlisted" | "duplicate" | null

export function EventRegistrationForm({
  orgId,
  event,
  ticketTypes,
  primaryColor,
}: EventRegistrationFormProps) {
  const [summaryLoading, setSummaryLoading] = useState(true)
  const [confirmedHeadcount, setConfirmedHeadcount] = useState(0)
  const [capacity, setCapacity] = useState<number | null>(event.capacity)

  const [name, setName] = useState("")
  const [email, setEmail] = useState("")
  const [ticketTypeId, setTicketTypeId] = useState<string>(
    ticketTypes.length === 1 ? ticketTypes[0].id : ""
  )
  const [headcount, setHeadcount] = useState("1")
  const [submitting, setSubmitting] = useState(false)
  const [outcome, setOutcome] = useState<Outcome>(null)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)

  const activeTypes = useMemo(() => ticketTypes.filter((t) => t.id), [ticketTypes])

  const selectedType = useMemo(
    () => activeTypes.find((t) => t.id === ticketTypeId) ?? null,
    [activeTypes, ticketTypeId]
  )

  const deadlinePassed = useMemo(() => {
    if (!event.registration_deadline) return false
    return new Date(event.registration_deadline) < new Date()
  }, [event.registration_deadline])

  const atCapacity = useMemo(() => {
    if (capacity == null) return false
    return confirmedHeadcount >= capacity
  }, [capacity, confirmedHeadcount])

  const loadSummary = useCallback(async () => {
    setSummaryLoading(true)
    const { data, error } = await supabase.rpc("event_registration_public_summary", {
      p_event_id: event.id,
    })
    setSummaryLoading(false)
    if (error) {
      setConfirmedHeadcount(0)
      setCapacity(event.capacity)
      return
    }
    const row = data as { confirmed_headcount?: number; capacity?: number | null } | null
    setConfirmedHeadcount(typeof row?.confirmed_headcount === "number" ? row.confirmed_headcount : 0)
    setCapacity(row?.capacity ?? event.capacity)
  }, [event.id, event.capacity])

  useEffect(() => {
    void loadSummary()
  }, [loadSummary])

  const showHeadcount =
    selectedType == null || selectedType.seats_per_ticket <= 1

  const submitIsWaitlist = atCapacity && event.waitlist_enabled && !deadlinePassed

  const submitLabel = submitIsWaitlist ? "Join waitlist" : "Register"

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (outcome) return
    if (deadlinePassed) return
    if (atCapacity && !event.waitlist_enabled) return

    const emailVal = email.trim()
    const nameVal = name.trim()
    if (!emailVal || !nameVal) {
      setErrorMessage("Name and email are required.")
      return
    }
    if (activeTypes.length > 1 && !ticketTypeId) {
      setErrorMessage("Select a ticket type.")
      return
    }

    setSubmitting(true)
    setErrorMessage(null)

    let hc = parseInt(headcount, 10)
    if (!Number.isFinite(hc) || hc < 1) hc = 1
    if (selectedType && selectedType.seats_per_ticket > 1) hc = 1

    try {
      const { data, error: fnError } = await supabase.functions.invoke("rsvp-event", {
        body: {
          action: "register",
          org_id: orgId,
          event_id: event.id,
          email: emailVal,
          name: nameVal,
          headcount: hc,
          ticket_type_id:
            activeTypes.length > 0 ? ticketTypeId || activeTypes[0]?.id : undefined,
        },
      })

      if (fnError) {
        let message = fnError.message ?? "Registration failed"
        if (fnError instanceof FunctionsHttpError && fnError.context) {
          try {
            const res = fnError.context as Response
            const body = (await res.json()) as { error?: string }
            if (typeof body?.error === "string") message = body.error
          } catch {
            // keep default
          }
        }
        setErrorMessage(message)
        setSubmitting(false)
        return
      }

      const status = (data as { status?: string })?.status
      if (status === "duplicate") {
        setOutcome("duplicate")
        setSubmitting(false)
        return
      }
      if (status === "full") {
        setSubmitting(false)
        setErrorMessage("This event is at capacity.")
        return
      }
      if (status === "closed") {
        setSubmitting(false)
        setErrorMessage("Registration is closed.")
        return
      }
      if (status === "waitlisted") {
        setOutcome("waitlisted")
        setSubmitting(false)
        void loadSummary()
        return
      }
      if (status === "confirmed") {
        setOutcome("confirmed")
        setSubmitting(false)
        void loadSummary()
        return
      }

      setOutcome("confirmed")
      setSubmitting(false)
      void loadSummary()
    } catch (err) {
      setErrorMessage(err instanceof Error ? err.message : "Something went wrong")
      setSubmitting(false)
    }
  }

  if (summaryLoading) {
    return (
      <Card className="border-2" style={{ borderColor: `${primaryColor}30` }}>
        <CardContent className="flex justify-center py-10">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    )
  }

  if (outcome === "confirmed") {
    return (
      <Card className="border-2" style={{ borderColor: `${primaryColor}30` }}>
        <CardContent className="p-6 space-y-2">
          <h3 className="text-xl font-semibold" style={{ color: primaryColor }}>
            You&apos;re registered!
          </h3>
          <p className="text-muted-foreground">Check your email for confirmation.</p>
        </CardContent>
      </Card>
    )
  }

  if (outcome === "waitlisted") {
    return (
      <Card className="border-2" style={{ borderColor: `${primaryColor}30` }}>
        <CardContent className="p-6 space-y-2">
          <h3 className="text-xl font-semibold" style={{ color: primaryColor }}>
            You&apos;re on the waitlist!
          </h3>
          <p className="text-muted-foreground">
            We&apos;ll email you if a spot opens.
          </p>
        </CardContent>
      </Card>
    )
  }

  if (outcome === "duplicate") {
    return (
      <Card className="border-2" style={{ borderColor: `${primaryColor}30` }}>
        <CardContent className="p-6 space-y-2">
          <p className="text-destructive" role="alert">
            This email is already registered for this event.
          </p>
        </CardContent>
      </Card>
    )
  }

  if (deadlinePassed) {
    return (
      <Card className="border-2" style={{ borderColor: `${primaryColor}30` }}>
        <CardContent className="p-6">
          <h3 className="text-xl font-semibold" style={{ color: primaryColor }}>
            Registration closed
          </h3>
          <p className="mt-2 text-muted-foreground">Registration is closed for this event.</p>
        </CardContent>
      </Card>
    )
  }

  if (atCapacity && !event.waitlist_enabled) {
    return (
      <Card className="border-2" style={{ borderColor: `${primaryColor}30` }}>
        <CardContent className="p-6">
          <h3 className="text-xl font-semibold" style={{ color: primaryColor }}>
            This event is at capacity
          </h3>
          <p className="mt-2 text-muted-foreground">
            All spots are filled and the waitlist is not available.
          </p>
        </CardContent>
      </Card>
    )
  }

  return (
    <Card className="border-2" style={{ borderColor: `${primaryColor}30` }}>
      <CardContent className="p-6">
        <h3 className="mb-4 text-xl font-semibold" style={{ color: primaryColor }}>
          {submitIsWaitlist ? "Join the waitlist" : "Register for this event"}
        </h3>
        <form onSubmit={(e) => void handleSubmit(e)} className="space-y-4 max-w-md">
          {activeTypes.length > 1 && (
            <div className="space-y-2">
              <Label>Ticket type</Label>
              <Select value={ticketTypeId} onValueChange={setTicketTypeId} required>
                <SelectTrigger>
                  <SelectValue placeholder="Select type" />
                </SelectTrigger>
                <SelectContent>
                  {activeTypes.map((t) => (
                    <SelectItem key={t.id} value={t.id}>
                      {t.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          <div className="space-y-2">
            <Label htmlFor="reg-name">
              Name <span className="text-destructive">*</span>
            </Label>
            <Input
              id="reg-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              autoComplete="name"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="reg-email">
              Email <span className="text-destructive">*</span>
            </Label>
            <Input
              id="reg-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoComplete="email"
            />
          </div>
          {showHeadcount && (
            <div className="space-y-2">
              <Label htmlFor="reg-headcount">Number of guests (including you)</Label>
              <Input
                id="reg-headcount"
                type="number"
                min={1}
                max={500}
                value={headcount}
                onChange={(e) => setHeadcount(e.target.value)}
              />
            </div>
          )}
          {errorMessage && (
            <p className="text-sm text-destructive" role="alert">
              {errorMessage}
            </p>
          )}
          <Button
            type="submit"
            disabled={submitting}
            style={{ backgroundColor: primaryColor }}
          >
            {submitting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Submitting...
              </>
            ) : (
              submitLabel
            )}
          </Button>
        </form>
      </CardContent>
    </Card>
  )
}
